import React from 'react';
import { motion } from 'framer-motion';
import { TrustBadges } from '../components/TrustBadges';
import { PricingCards } from '../components/PricingCards';
import { BookOpen, Smartphone, Zap, Layers, Users, Headphones, ArrowRight } from 'lucide-react';
const features = [{
  icon: Layers,
  title: 'Vast Selection',
  description: 'Thousands of titles across genres, curated for quality and relevance.'
}, {
  icon: Zap,
  title: 'Instant Access',
  description: 'Start reading immediately. No downloads, no waiting, just content.'
}, {
  icon: BookOpen,
  title: 'Flexible Plans',
  description: 'Choose from monthly or annual subscriptions that suit your reading habits.'
}, {
  icon: Users,
  title: 'Business Solutions',
  description: 'Comprehensive team access plans for organisations and educational bodies.'
}, {
  icon: Smartphone,
  title: 'Read Anywhere',
  description: 'Seamless experience across desktop, tablet, and mobile devices.'
}, {
  icon: Headphones,
  title: 'Expert Support',
  description: 'Dedicated customer service team ready to assist with any queries.'
}];
const steps = [{
  number: '01',
  title: 'Choose Your Plan',
  description: 'Select the subscription tier that perfectly fits your reading needs and budget.'
}, {
  number: '02',
  title: 'Access Your Library',
  description: 'Get instant access to our comprehensive digital catalogue of premium ebooks.'
}, {
  number: '03',
  title: 'Read Anywhere',
  description: 'Enjoy your content on any device, anytime, with our responsive reader.'
}];
export function HomePage() {
  return <main className="flex-1 bg-background" data-id="element-230">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-24 lg:pt-32 lg:pb-40" data-id="element-231">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 -z-10" data-id="element-232" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-slate-50/50 skew-x-12 -z-10 transform origin-top" data-id="element-233" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-234">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-235">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-236">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-dark leading-tight mb-6" data-id="element-237">
                Unlimited Reading <br data-id="element-238" />
                <span className="text-primary" data-id="element-239">Made Simple.</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-240">
                Bluecore gives you fast, subscription-based access to a growing
                digital library.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-241">
                <button className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl active:transform active:scale-95 flex items-center justify-center gap-2" data-id="element-242">
                  Contact Us
                  <ArrowRight className="w-5 h-5" data-id="element-243" />
                </button>
                <button className="px-8 py-4 rounded-lg font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 transition-all border border-transparent hover:border-primary/20" data-id="element-244">
                  View Plans
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-245">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl bg-slate-200 relative group" data-id="element-246">
                <img src="https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&q=80&w=1000" alt="Digital library interface on tablet" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-247" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" data-id="element-248" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-accent/10 rounded-full blur-2xl" data-id="element-249" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-2xl" data-id="element-250" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white" data-id="element-251">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-252">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-253">
            <h2 className="text-3xl font-bold text-slate-dark mb-6" data-id="element-254">
              Why Choose Bluecore?
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed mb-12" data-id="element-255">
              Bluecore provides subscription-based access to curated ebooks,
              giving readers and businesses fast access to digital content
              without downloads or complex setup. Our platform makes building
              your digital library simple and straightforward.
            </p>
            <TrustBadges data-id="element-256" />
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50" data-id="element-257">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-258">
          <div className="text-center mb-16" data-id="element-259">
            <h2 className="text-3xl font-bold text-slate-dark mb-4" data-id="element-260">
              How It Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-261">
              Get started with Bluecore in three simple steps. No complicated
              setup required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-262">
            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 relative overflow-hidden group hover:shadow-md transition-shadow" data-id="element-263">
                <div className="absolute top-0 right-0 text-9xl font-bold text-slate-50 -mr-8 -mt-8 select-none transition-colors group-hover:text-primary/5" data-id="element-264">
                  {step.number}
                </div>
                <div className="relative z-10" data-id="element-265">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6 text-primary font-bold text-xl" data-id="element-266">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-slate-dark mb-3" data-id="element-267">
                    {step.title}
                  </h3>
                  <p className="text-slate-600" data-id="element-268">{step.description}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white" data-id="element-269">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-270">
          <div className="text-center mb-16" data-id="element-271">
            <h2 className="text-3xl font-bold text-slate-dark mb-4" data-id="element-272">
              Choose Your Plan
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-273">
              Select the subscription that fits your reading needs. All plans
              include instant access and multi-device support.
            </p>
          </div>
          <PricingCards data-id="element-274" />
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-slate-50" data-id="element-275">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276">
          <div className="text-center mb-16" data-id="element-277">
            <h2 className="text-3xl font-bold text-slate-dark mb-4" data-id="element-278">
              Platform Benefits
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-279">
              Everything you need for a seamless digital reading experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-280">
            {features.map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.4
          }} className="flex gap-4 p-6 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-281">
                <div className="flex-shrink-0" data-id="element-282">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center text-accent" data-id="element-283">
                    <feature.icon className="w-6 h-6" data-id="element-284" />
                  </div>
                </div>
                <div data-id="element-285">
                  <h3 className="font-bold text-slate-dark mb-2" data-id="element-286">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-287">
                    {feature.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-288">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1507842217121-9eac929ca009?auto=format&fit=crop&q=80&w=2000')] bg-cover bg-center opacity-10" data-id="element-289" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/80" data-id="element-290" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-291">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-292">
            Ready to Start Reading?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-293">
            Join Bluecore today and unlock unlimited access to our digital
            library. Experience the future of reading.
          </p>
          <button className="bg-accent hover:bg-accent-dark text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-accent/20 active:transform active:scale-95" data-id="element-294">
            Contact Us
          </button>
        </div>
      </section>
    </main>;
}